import styles from './AutoPersistStatusIndicator.module.css';
import type { AutoPersistStatus } from './useAutoPersistWorkflow';

interface AutoPersistStatusIndicatorProps {
  status: AutoPersistStatus;
  /** Wall-clock time of the last successful save, or null if none yet. */
  lastSavedAt: number | null;
}

// Render a short phrase describing the current save status.
function statusMessage(
  status: AutoPersistStatus,
  lastSavedAt: number | null
): string {
  if (status === 'saving' || status === 'pending') {
    return 'Saving…';
  }
  if (status === 'error') {
    return "Couldn't save — will retry on next edit";
  }
  if (lastSavedAt === null) {
    return '';
  }
  return `Saved ${formatRelativeTime(lastSavedAt)}`;
}

// Human-friendly relative time ("just now", "2m ago", "1h ago").
// Static — doesn't tick; users see the label update on the next save.
function formatRelativeTime(timestamp: number): string {
  const deltaSeconds = Math.floor((Date.now() - timestamp) / 1000);
  if (deltaSeconds < 10) {
    return 'just now';
  }
  if (deltaSeconds < 60) {
    return `${deltaSeconds}s ago`;
  }
  const deltaMinutes = Math.floor(deltaSeconds / 60);
  if (deltaMinutes < 60) {
    return `${deltaMinutes}m ago`;
  }
  const deltaHours = Math.floor(deltaMinutes / 60);
  return `${deltaHours}h ago`;
}

/**
 * Shows the current auto-save state next to the workflow controls.
 * Muted text for idle/saved; "Saving…" while a save is pending or in flight;
 * error style when the last save failed.
 */
export function AutoPersistStatusIndicator({
  status,
  lastSavedAt,
}: AutoPersistStatusIndicatorProps) {
  const message = statusMessage(status, lastSavedAt);
  if (message === '') {
    return null;
  }
  const className =
    status === 'error' ? styles.errorStatus : styles.mutedStatus;
  return <span className={className}>{message}</span>;
}
